// Vanilla CSS for DropdownActivityMaterialContent — glass inline-select pill design.
// Prefix: dds2-
// Built on Playful Frost tokens: --catchup-*, --brand-*, --color-success*/--color-error*,
// --fs-*, --rad-catchup-*, --space-*, --glass-*.
export const dropdownActivityMaterialContentStyles = `
  /* Root wrapper — numbered blank list */
  .dds2-root { display: flex; flex-direction: column; gap: var(--space-4); }

  /* Blank rows */
  .dds2-row { display: flex; align-items: flex-start; gap: var(--space-3); }
  .dds2-num {
    flex-shrink: 0;
    width: 28px;
    font-size: var(--fs-base);
    font-weight: 700;
    color: var(--text-muted);
    padding-top: 6px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  /* Field holds the pill + optional fix-below */
  .dds2-field { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; min-width: 0; }

  /* Inline select pill wrapper — relative for the popover */
  .dds2-wrap { position: relative; display: inline-flex; flex-direction: column; align-items: flex-start; gap: 4px; }

  /* The visible pill / trigger */
  .dds2-sel {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-weight: 700;
    font-size: var(--fs-base);
    color: var(--brand-dark);
    padding: 5px 12px;
    border-radius: var(--rad-catchup-full);
    background: var(--brand-50);
    border: 1.5px solid var(--catchup-blue);
    line-height: 1.4;
    white-space: nowrap;
    transition: border-color 150ms, background 150ms, box-shadow 150ms;
    user-select: none;
  }
  .dds2-sel::after {
    content: "\\25BE";
    font-size: 11px;
    color: var(--text-muted);
    flex-shrink: 0;
  }

  /* Empty (no answer yet) */
  .dds2-sel.dds2-empty {
    color: var(--text-muted);
    background: var(--catchup-white);
    border: 1.5px solid var(--catchup-blue-200);
    font-weight: 600;
  }

  /* Correct answer state */
  .dds2-sel.dds2-correct {
    background: linear-gradient(160deg, rgba(122,203,164,0.42), rgba(94,170,130,0.24));
    border-color: var(--color-success);
    color: var(--color-success-text);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    cursor: default;
  }

  /* Wrong answer state */
  .dds2-sel.dds2-wrong {
    background: linear-gradient(160deg, rgba(232,150,128,0.4), rgba(217,97,63,0.22));
    border-color: var(--color-error);
    color: var(--color-error-text);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    cursor: default;
  }

  /* Correct answer revealed below a wrong answer */
  .dds2-fix {
    font-size: var(--fs-lg);
    font-weight: 700;
    line-height: 1.2;
    color: var(--accent-lime-text, var(--color-success-text));
    white-space: nowrap;
    padding-left: 2px;
  }
  .dds2-fix::before { content: "\\2713 "; font-size: var(--fs-base); }

  /* Graded image / audio blank: the learner's pick and, unless it was right, the
     correct option beside it. Frames carry the verdict colour; amber = left blank. */
  .dds2-media-graded { display: flex; flex-wrap: wrap; align-items: flex-start; gap: var(--space-3); }
  .dds2-media-card {
    position: relative;
    display: inline-flex; flex-direction: column; align-items: flex-start; gap: 6px;
    padding: 8px; border-radius: var(--rad-catchup-large);
    border: 2px solid var(--catchup-blue-200); background: var(--catchup-white);
    max-width: 100%;
  }
  .dds2-media-card.is-correct { border-color: var(--color-success); background: var(--color-success-bg); }
  .dds2-media-card.is-wrong { border-color: var(--color-error); background: var(--color-error-bg); }
  .dds2-media-card.is-blank { border-color: var(--color-warning); background: var(--color-warning-bg); }
  .dds2-media-card.is-key { border-style: dashed; border-color: var(--color-success); }
  .dds2-media-body { display: block; max-width: 100%; }
  .dds2-media-empty {
    display: flex; align-items: center; justify-content: center;
    width: var(--w-catchup-activity-media-box-item); height: var(--h-catchup-activity-media-box-item);
    max-width: 100%;
    font-size: var(--fs-base); font-weight: 700; color: var(--color-warning-text);
  }
  .dds2-media-mark {
    position: absolute; top: -10px; right: -10px;
    width: 24px; height: 24px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 800; color: var(--catchup-white);
  }
  .dds2-media-card.is-correct .dds2-media-mark { background: var(--color-success); }
  .dds2-media-card.is-wrong .dds2-media-mark { background: var(--color-error); }
  .dds2-media-card.is-blank .dds2-media-mark { background: var(--color-warning); }
  .dds2-media-tag { font-size: var(--fs-xs); font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-success-text); }

  /* Glass popover dropdown */
  .dds2-pop {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 30;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px;
    border-radius: var(--rad-catchup-large);
    /* No backdrop-filter: this popover is ALWAYS a scroll container (see max-height
       + overflow-y below), so a blur re-sampled it on every internal scroll frame —
       and it renders inside the activity solver, per dropdown blank. Near-opaque
       fill carries the frost instead. */
    background: var(--glass-popover-bg-strong, rgba(255,255,255,0.97));
    border: 1px solid var(--glass-border-top, rgba(255,255,255,0.9));
    box-shadow: var(--glass-popover-shadow, 0 8px 32px -8px rgba(15,72,89,0.32), inset 1px 1px 2px rgba(255,255,255,0.6));
    /* Always scrollable so a long option list is reachable in tight space. */
    max-height: min(280px, 40vh);
    overflow-y: auto;
  }

  /* Flip above the pill when it sits near the viewport bottom. */
  .dds2-pop.dds2-pop-up { top: auto; bottom: calc(100% + 6px); }

  /* Popover option button */
  .dds2-opt {
    cursor: pointer;
    text-align: left;
    white-space: nowrap;
    font-size: var(--fs-base);
    font-weight: 600;
    color: var(--text-secondary);
    padding: 7px 12px;
    border-radius: var(--rad-catchup-medium);
    border: none;
    background: transparent;
    font-family: var(--font-sans);
    transition: background 120ms, color 120ms;
  }
  .dds2-opt:hover { background: var(--brand-50); color: var(--brand-dark); }
  .dds2-opt.dds2-opt-sel { background: var(--brand-50); color: var(--brand-dark); }

  /* Responsive — ensure pop doesn't overflow on small screens */
  @media (max-width: 480px) {
    .dds2-sel { font-size: var(--fs-sm); padding: 4px 10px; }
    .dds2-pop { position: fixed; left: 16px; right: 16px; top: auto; }
    /* Fixed full-width sheet on small screens — the flip-up offset does not apply. */
    .dds2-pop.dds2-pop-up { bottom: auto; }
  }
`;
